import { Trans, useLingui } from '@lingui/react/macro';
import { AlertCircle, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { translateErrorCode } from '@/lib/error-utils';

interface QueryErrorProps {
  error: unknown;
  onRetry: () => void;
}

/**
 * Centered error state shown when an initial data load fails (A5 / D35) — e.g.
 * the API is unreachable on first paint. Mirrors `TemplatesEmptyState`'s layout
 * so a failed surface looks intentional rather than broken.
 *
 * The user-facing message is deliberately friendly. Raw technical strings (the
 * browser's "Failed to fetch", a stack-y `Error.message`, etc.) are never shown
 * — the actual error still reaches the console/error boundary for debugging.
 *   - Coded API errors (those exposing a `code`) keep their meaningful message,
 *     translated via the errors catalog where available (A14 / D49).
 *   - Network failures get connection-oriented guidance.
 *   - Anything else gets a generic "try again" message.
 */
export function QueryError({ error, onRetry }: QueryErrorProps) {
  const { t } = useLingui();

  const code = extractErrorCode(error);
  let message: string;
  if (code) {
    message = translateErrorCode(code, extractMessage(error) ?? t`Something went wrong.`);
  } else if (isLikelyNetworkError(error)) {
    message = t`We couldn't reach the server. Check your connection and try again.`;
  } else {
    message = t`We couldn't load this right now. Please try again.`;
  }

  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12 text-center">
      <AlertCircle className="text-destructive h-10 w-10" />
      <h3 className="text-[length:var(--section-title-font-size)] leading-[var(--section-title-line-height)] font-semibold">
        <Trans>Something went wrong</Trans>
      </h3>
      <p className="text-muted-foreground max-w-md text-sm">{message}</p>
      <Button variant="outline" onClick={onRetry}>
        <RotateCcw className="mr-2 h-4 w-4" /> <Trans>Try again</Trans>
      </Button>
    </div>
  );
}

/**
 * Narrows an unknown error to a `{ code: string }` shape without
 * type-asserting. `RotagenApiError` (and other Rotaready API error types)
 * expose `code`; plain `Error`s don't, so we treat the absent / non-string
 * case as "no code available".
 */
function extractErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const candidate: unknown = (error as Record<string, unknown>).code;
  return typeof candidate === 'string' ? candidate : undefined;
}

/** Reads a `message` string off an `Error` or a plain `{ message }` object. */
function extractMessage(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.message || undefined;
  }
  if (typeof error === 'object' && error !== null) {
    const candidate: unknown = (error as Record<string, unknown>).message;
    return typeof candidate === 'string' && candidate ? candidate : undefined;
  }
  return undefined;
}

/**
 * Heuristic for "the request never reached the server" failures. `fetch`
 * rejects with a `TypeError` on network errors, and the message varies by
 * engine ("Failed to fetch", "NetworkError…", "Load failed").
 */
function isLikelyNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return true;
  }
  if (error instanceof Error) {
    return /failed to fetch|network ?error|load failed/i.test(error.message);
  }
  return false;
}
